import React, { useMemo } from 'react';
import { Button, DatePicker, Space, Typography, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { ResetIcon } from '../../common/ActionIcons';
import SingleEntitySelectMiniature from '../../crud/SingleEntitySelectMiniature.component';
import { listCohorts, listCohortsByIds } from '../../../actions/cohortActions';
import { listUploadBatches, listUploadBatchesByIds } from '../../../actions/uploadBatchActions';

const { Text } = Typography;

/**
 * Page-level reporting filters (SQL tab only). Passed into all chart function executes.
 */
export default function ReportingGlobalFilters({
    dateRange,
    onDateRangeChange,
    cohortId = null,
    onCohortIdChange,
    uploadBatchId = null,
    onUploadBatchIdChange,
    onReset,
}) {
    const { t } = useTranslation();
    const screens = Grid.useBreakpoint();
    const stack = !screens.lg;

    const hasActiveFilters = useMemo(
        () => Boolean(dateRange?.[0] || dateRange?.[1] || cohortId || uploadBatchId),
        [dateRange, cohortId, uploadBatchId],
    );

    const fieldStyle = { minWidth: stack ? '100%' : 200, flex: stack ? undefined : '1 1 200px' };

    return (
        <Space
            direction={stack ? 'vertical' : 'horizontal'}
            size={stack ? 12 : 16}
            wrap
            align={stack ? 'stretch' : 'end'}
            style={{ width: '100%', marginBottom: 16 }}
        >
            <div style={fieldStyle}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                    {t('Date range')}
                </Text>
                <DatePicker.RangePicker
                    value={dateRange}
                    onChange={onDateRangeChange}
                    allowClear
                    placeholder={[t('From'), t('To')]}
                    showTime={{ format: 'HH:mm' }}
                    format="YYYY-MM-DD HH:mm"
                    style={{ width: '100%' }}
                />
            </div>
            <div style={fieldStyle}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                    {t('Session')}
                </Text>
                <SingleEntitySelectMiniature
                    singularEntity="cohort"
                    pluralEntity="cohorts"
                    constantPrefix="COHORT"
                    listEntities={listCohorts}
                    listEntitiesByIds={listCohortsByIds}
                    value={cohortId}
                    onChange={onCohortIdChange}
                    primarySearchProp="cohortName"
                    secondarySearchProp="description"
                    placeholder={t('All sessions')}
                    enableCreate={false}
                    enableEditing={false}
                />
            </div>
            <div style={fieldStyle}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                    {t('Upload batch')}
                </Text>
                <SingleEntitySelectMiniature
                    singularEntity="uploadBatch"
                    pluralEntity="uploadBatches"
                    constantPrefix="UPLOAD_BATCH"
                    listEntities={listUploadBatches}
                    listEntitiesByIds={listUploadBatchesByIds}
                    value={uploadBatchId}
                    onChange={onUploadBatchIdChange}
                    primarySearchProp="fileName"
                    secondarySearchProp="fileFormat"
                    placeholder={t('All upload batches')}
                    enableCreate={false}
                    enableEditing={false}
                />
            </div>
            {onReset && (
                <Button
                    icon={<ResetIcon />}
                    onClick={onReset}
                    disabled={!hasActiveFilters}
                >
                    {t('Reset')}
                </Button>
            )}
        </Space>
    );
}

/** Normalise page-level filter ids for executeReportFunction. */
export function buildReportingGlobalFilters(cohortId, uploadBatchId, organisationId) {
    const filters = {};
    if (cohortId) filters.cohortId = cohortId;
    if (uploadBatchId) filters.uploadBatchId = uploadBatchId;
    if (organisationId) filters.organisationId = organisationId;
    return filters;
}
